import { WDD } from '../core/wdd.js';

export function variants() {
    const variantsRoot = WDD.el.querySelector('.bp-variants');
    const detailImage = WDD.el.querySelector('.bp-detail__image');
    const detailPicture = detailImage?.tagName === 'PICTURE' ? detailImage : detailImage?.closest('picture');
    const detailSource = detailPicture?.querySelector('source[type="image/avif"]');
    const detailImg = detailPicture?.querySelector('img');
    const buyGroup = variantsRoot?.querySelector('.bp-variants__group--buy');
    const buyLink = buyGroup?.querySelector('.bp-variants__link');
    const buyButton = buyGroup?.querySelector('.bp-button--buy');

    const colorButtons = Array.from(variantsRoot?.querySelectorAll('.bp-color-swatches .bp-color-swatch') || []);
    const secondaryButtons = Array.from(
        variantsRoot?.querySelectorAll('.bp-variants__group--storage .bp-pill-group .bp-button--pill, .bp-variants__group--size .bp-pill-group .bp-button--pill') || []
    );

    if (!variantsRoot || !colorButtons.length || !secondaryButtons.length || !buyLink || !buyButton || !detailPicture || !detailSource || !detailImg) return;

    const IMAGE_BASE = 'https://image.alza.cz/Foto/or/bp/google/pixel-11/img';
    const hiddenClass = 'bp-detail__image--hidden';
    const family = detectFamily();
    let swapToken = 0;

    const wait = ms => new Promise(resolve => window.setTimeout(resolve, ms));

    const waitForDetailImage = () => new Promise(resolve => {
        const onLoad = async () => {
            detailImg.removeEventListener('error', onError);
            resolve(true);
        };
        const onError = () => {
            detailImg.removeEventListener('load', onLoad);
            resolve(false);
        };
        detailImg.addEventListener('load', onLoad, { once: true });
        detailImg.addEventListener('error', onError, { once: true });
    });

    const parseOids = element => String(element.getAttribute('data-oid') || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

    const buttonOids = new Map();
    [...colorButtons, ...secondaryButtons].forEach(button => {
        buttonOids.set(button, new Set(parseOids(button)));
    });

    const getActiveColorButton = () => colorButtons.find(button => button.classList.contains('bp-color-swatch--active')) || colorButtons[0];
    const getActiveSecondaryButton = () => secondaryButtons.find(button => button.classList.contains('bp-button--pill-active')) || secondaryButtons[0];

    const getOids = button => buttonOids.get(button) || new Set();

    const intersectSets = (...sets) => {
        const validSets = sets.filter(set => set && set.size);
        if (!validSets.length) return new Set();

        const [firstSet, ...restSets] = validSets;
        const result = new Set();
        firstSet.forEach(value => {
            if (restSets.every(set => set.has(value))) result.add(value);
        });
        return result;
    };

    const hasIntersection = (left, right) => {
        for (const value of left) {
            if (right.has(value)) return true;
        }
        return false;
    };

    const getCompatibleButtons = (buttons, referenceButton) => {
        if (!referenceButton) return buttons.slice();
        const referenceOids = getOids(referenceButton);
        return buttons.filter(button => hasIntersection(getOids(button), referenceOids));
    };

    const setActiveButton = (buttons, activeButton, activeClass) => {
        buttons.forEach(button => button.classList.toggle(activeClass, button === activeButton));
    };

    const setHiddenState = (buttons, visibleButtons) => {
        const visibleSet = new Set(visibleButtons);
        buttons.forEach(button => {
            const shouldHide = !visibleSet.has(button);
            button.hidden = shouldHide;
            button.setAttribute('aria-hidden', String(shouldHide));
        });
    };

    const updateVoucher = (colorButton, secondaryButton) => {
        const activeButtons = [colorButton, secondaryButton];
        const voucherButton = activeButtons.find(button => button.hasAttribute('data-voucher'));
        const voucher = voucherButton?.getAttribute('data-voucher');

        if (voucher) buyButton.setAttribute('data-voucher', voucher);
        else buyButton.removeAttribute('data-voucher');
    };

    function detectFamily() {
        const href = buyLink?.getAttribute('href') || '';
        if (href.includes('google-pixel-watch-5')) return 'pixel-watch-5';
        if (href.includes('google-pixel-11-pro-xl')) return 'pixel-11-pro-xl';
        if (href.includes('google-pixel-11-pro')) return 'pixel-11-pro';
        return 'pixel-11';
    }

    function getProductLabel() {
        switch (family) {
            case 'pixel-watch-5': return 'Google Pixel Watch 5';
            case 'pixel-11-pro-xl': return 'Google Pixel 11 Pro XL';
            case 'pixel-11-pro': return 'Google Pixel 11 Pro';
            default: return 'Google Pixel 11';
        }
    }

    function getFamilySlug() {
        switch (family) {
            case 'pixel-watch-5': return 'google-pixel-watch-5';
            case 'pixel-11-pro-xl': return 'google-pixel-11-pro-xl';
            case 'pixel-11-pro': return 'google-pixel-11-pro';
            default: return 'google-pixel-11';
        }
    }

    function getImageFolder(secondaryValue) {
        if (family === 'pixel-watch-5') {
            return `${IMAGE_BASE}/variant-pixel-watch-5-${slugify(secondaryValue)}`;
        }

        return `${IMAGE_BASE}/variant-${family}`;
    }

    function slugify(value) {
        return String(value).toLowerCase().replace(/\s+/g, '');
    }

    function buildHref(secondaryValue, colorValue, oid) {
        return `/${getFamilySlug()}-${slugify(secondaryValue)}-${String(colorValue).toLowerCase()}-d${oid}.htm`;
    }

    function buildAlt(secondaryValue, colorLabel) {
        return `${getProductLabel()} ${secondaryValue} ${colorLabel}`;
    }

    function buildImageUrls(secondaryValue, colorValue) {
        const folder = getImageFolder(secondaryValue);
        const file = String(colorValue).toLowerCase();

        return {
            avif: `${folder}/${file}.avif`,
            avif2x: `${folder}/${file}@2x.avif`,
            png: `${folder}/${file}.png`,
            png2x: `${folder}/${file}@2x.png`,
        };
    }

    function getCommonOid(colorButton, secondaryButton) {
        const common = intersectSets(getOids(colorButton), getOids(secondaryButton));
        return common.values().next().value || '';
    }

    async function swapDetailImage(secondaryValue, colorValue, altText) {
        const token = ++swapToken;
        const urls = buildImageUrls(secondaryValue, colorValue);

        if (
            detailSource.getAttribute('srcset') === `${urls.avif}, ${urls.avif2x} 2x`
            && detailImg.getAttribute('src') === urls.png
            && detailImg.getAttribute('srcset') === `${urls.png}, ${urls.png2x} 2x`
        ) {
            return;
        }

        detailPicture.classList.add(hiddenClass);
        await wait(500);

        if (token !== swapToken) return;

        const imageReady = waitForDetailImage();
        detailSource.setAttribute('srcset', `${urls.avif}, ${urls.avif2x} 2x`);
        detailImg.src = urls.png;
        detailImg.setAttribute('srcset', `${urls.png}, ${urls.png2x} 2x`);
        detailImg.alt = altText;

        const didLoad = await imageReady;

        if (token !== swapToken || !didLoad) return;

        detailPicture.classList.remove(hiddenClass);
    }

    function applySelection(sourceGroup) {
        const activeColorButton = getActiveColorButton();
        const activeSecondaryButton = getActiveSecondaryButton();

        let nextColorButton = activeColorButton;
        let nextSecondaryButton = activeSecondaryButton;

        if (sourceGroup === 'color') {
            nextColorButton = colorButtons.find(button => button.classList.contains('bp-color-swatch--active')) || activeColorButton;
            const compatibleSecondaries = getCompatibleButtons(secondaryButtons, nextColorButton);
            if (!compatibleSecondaries.includes(nextSecondaryButton)) {
                nextSecondaryButton = compatibleSecondaries[0] || nextSecondaryButton;
            }
        }
        else if (sourceGroup === 'secondary') {
            nextSecondaryButton = secondaryButtons.find(button => button.classList.contains('bp-button--pill-active')) || activeSecondaryButton;
            const compatibleColors = getCompatibleButtons(colorButtons, nextSecondaryButton);
            if (!compatibleColors.includes(nextColorButton)) {
                nextColorButton = compatibleColors[0] || nextColorButton;
            }
        }

        const resolvedCommon = getCommonOid(nextColorButton, nextSecondaryButton);
        const resolvedColorValue = nextColorButton.getAttribute('data-color') || '';
        const resolvedSecondaryValue = nextSecondaryButton.textContent.trim();
        const resolvedColorLabel = nextColorButton.getAttribute('aria-label') || resolvedColorValue;

        setActiveButton(colorButtons, nextColorButton, 'bp-color-swatch--active');
        setActiveButton(secondaryButtons, nextSecondaryButton, 'bp-button--pill-active');

        setHiddenState(colorButtons, getCompatibleButtons(colorButtons, nextSecondaryButton));
        setHiddenState(secondaryButtons, getCompatibleButtons(secondaryButtons, nextColorButton));
        updateVoucher(nextColorButton, nextSecondaryButton);

        if (resolvedCommon) {
            buyButton.setAttribute('data-oid', resolvedCommon);
            buyLink.setAttribute('href', buildHref(resolvedSecondaryValue, resolvedColorValue, resolvedCommon));
            buyLink.textContent = `${getProductLabel()} ${resolvedSecondaryValue} ${resolvedColorLabel}`;
        }

        void swapDetailImage(resolvedSecondaryValue, resolvedColorValue, buildAlt(resolvedSecondaryValue, resolvedColorLabel));
    }

    colorButtons.forEach(button => {
        button.addEventListener('click', () => {
            if (button.classList.contains('bp-color-swatch--active')) return;
            setActiveButton(colorButtons, button, 'bp-color-swatch--active');
            applySelection('color');
        });
    });

    secondaryButtons.forEach(button => {
        button.addEventListener('click', () => {
            if (button.classList.contains('bp-button--pill-active')) return;
            setActiveButton(secondaryButtons, button, 'bp-button--pill-active');
            applySelection('secondary');
        });
    });

    applySelection();
}
